import { useLocation } from "wouter";
import { ChevronIcon } from "../../design/Icons";
import { palette } from "../../design/tokens";
import { t } from "../../locale";
import { useGame } from "../../stores/game";

/**
 * Common frame for every account screen.
 *
 * plan.md §5 blocked every account screen while in a room, because registering
 * or signing in swapped the player's identity and orphaned their membership.
 * That is now handled per screen — linking keeps the identity, signing in gives
 * the room up first — so the blanket guard is opt-out. See plan.md §5.14.
 *
 * It still lives here rather than in the route table: being in a room is a
 * state the player can enter *after* the screen has opened, from another tab or
 * a reconnect.
 */
export const AuthShell = ({
  title,
  error,
  allowInRoom = false,
  children,
  footer,
}: {
  title: string;
  error?: string | null;
  /**
   * Set by screens that handle the room themselves — either because they keep
   * the same identity (linking a guest to an email) or because they leave the
   * room before changing it (signing in).
   */
  allowInRoom?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) => {
  const [, navigate] = useLocation();
  const inRoom = useGame((s) => s.phase !== null);

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col px-5 pb-8 pt-6">
      <button
        onClick={() => navigate("/")}
        className="flex min-h-0 min-w-0 items-center gap-1 self-start py-2 pr-2 text-sm text-ink-dim"
      >
        <ChevronIcon direction="left" color={palette.inkDim} size={16} />
        {t.auth.back}
      </button>

      <h1 className="mt-4 text-2xl">{title}</h1>

      {inRoom && !allowInRoom ? (
        <div className="mt-8 flex flex-col gap-4">
          <p className="text-sm text-ink-dim">{t.auth.blockedInRoom}</p>
          <button
            className="w-full bg-surface px-4 py-3 text-base font-bold uppercase tracking-tight text-ink"
            onClick={() => navigate("/room")}
          >
            {t.auth.backToRoom}
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-col gap-4">{children}</div>
          {error && (
            <p className="mt-4 text-center text-sm text-side-b">{error}</p>
          )}
          {footer && (
            <div className="mt-6 flex flex-col items-center gap-3 text-xs text-ink-dim">
              {footer}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/** Shared field styling, so six forms cannot drift apart. */
export const Field = ({
  label,
  type,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  type: "email" | "password";
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-widest text-ink-dim">
      {label}
    </span>
    <input
      className="w-full bg-surface px-4 py-3 text-base text-ink"
      type={type}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

export const SubmitButton = ({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) => (
  <button
    className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg disabled:opacity-40"
    disabled={disabled}
    onClick={onClick}
  >
    {label}
  </button>
);
